import { z } from 'zod';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { ProcessDraftStepSchema } from './process-draft-step.schema';
import { ProcessDraftAssignmentSchema } from './process-draft-assignment.schema';
import { ProcessDraftEventNodeSchema } from '../process-node';
/**
 * Draft Node Envelope
 *
 * The uniform response shape of every draft mutation endpoint: the server
 * reports exactly which draft-graph nodes it touched, each one
 * self-describing its collection via a `nodeKind` discriminator, so the
 * client cache writer never has to guess which of the three cache arrays
 * (`steps[]` / `draftAssignments[]` / `events[]`) a returned doc belongs to.
 *
 * None of these schemas may use `.refine`/`.superRefine`: bliksy's
 * `resolveSubSchema` cannot walk refine wrappers when resolving deep paths.
 */ /**
 * Wire arm for assignment nodes. `nodeKind` is stamped explicitly by the
 * backend at envelope-build time — it does NOT live on
 * {@link ProcessDraftAssignmentSchema} itself, because GET responses don't
 * carry the field at runtime and adding a defaulted literal there would be a
 * type lie.
 */ export const DraftAssignmentNodeWireSchema = ProcessDraftAssignmentSchema.extend({
    nodeKind: z.literal('assignment')
});
/**
 * One self-describing draft-graph node on the wire, discriminated by
 * `nodeKind`:
 * - `'step'` — {@link ProcessDraftStepSchema} (defaulted-literal arm, same
 *   pattern as `ProcessNodeSchema`)
 * - `'assignment'` — {@link DraftAssignmentNodeWireSchema}
 * - `'event'` — {@link ProcessDraftEventNodeSchema}, the draft event ROW
 *   shape (keyed by `stableStepId`), never the legacy `ProcessEventSchema`
 */ export const DraftNodeWireSchema = z.discriminatedUnion('nodeKind', [
    ProcessDraftStepSchema,
    DraftAssignmentNodeWireSchema,
    ProcessDraftEventNodeSchema
]);
/**
 * Envelope reconciliation rules:
 *
 * - `nodes` carries the full post-mutation docs of every node the mutation
 *   created or modified; the client upserts each into the cache array
 *   selected by its `nodeKind`.
 * - Kind-swaps (the convert endpoints) appear as the node under its NEW
 *   kind — the reconciler must evict the `stableStepId` from sibling arrays
 *   before upserting. Swaps are NOT listed in `deletedNodeIds`.
 * - `deletedNodeIds` are `stableStepId`s (NOT `_id`s) of nodes removed from
 *   the draft graph entirely. The `stableStepId` space is the draft graph's
 *   identity space, shared across steps, assignments and event rows.
 */ export const DraftNodeEnvelopeSchema = z.object({
    nodes: z.array(DraftNodeWireSchema),
    /** stableStepIds of nodes created by this mutation (placeholder-swap reconciliation). */ createdNodeIds: z.array(ObjectIdSchema),
    /** stableStepIds (NOT _ids) of nodes removed from the draft graph entirely. */ deletedNodeIds: z.array(ObjectIdSchema)
});

//# sourceMappingURL=draft-node-envelope.schema.js.map